import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";

import { marketplaceCardSx } from "../constants/marketplaceCardStyles";
import { marketplaceTypography as mt } from "../constants/marketplaceTypography";
import type { CartItem } from "../types";
import { formatPriceBrl } from "../utils/addressHelpers";

interface CheckoutProductsAccordionProps {
  items: CartItem[];
  onEdit?: () => void;
}

/** Lista colapsável de produtos — única fonte de itens no resumo. */
export function CheckoutProductsAccordion({
  items,
  onEdit,
}: CheckoutProductsAccordionProps) {
  const theme = useTheme();
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <Accordion
      defaultExpanded={items.length <= 3}
      disableGutters
      elevation={0}
      sx={{
        ...marketplaceCardSx(theme),
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon color="primary" />}
        sx={{
          minHeight: 56,
          px: 1.5,
          "& .MuiAccordionSummary-content": {
            my: 1,
            alignItems: "center",
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{ width: "100%", pr: 1 }}
        >
          <Typography sx={{ ...mt.label, color: "primary800.main" }}>
            Produtos ({itemCount} {itemCount === 1 ? "item" : "itens"})
          </Typography>
          {onEdit ? (
            <Button
              size="small"
              variant="text"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              sx={{ ...mt.bodySecondary, fontWeight: 700, textTransform: "none", minWidth: "auto", flexShrink: 0 }}
            >
              Alterar
            </Button>
          ) : null}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
        <Stack spacing={1.25}>
          {items.map((item) => (
            <Stack
              key={item.productId}
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={1}
            >
              <Typography sx={{ ...mt.bodySecondary, flex: 1 }}>
                {item.quantity}x {item.name}
              </Typography>
              <Typography sx={{ ...mt.bodySecondary, fontWeight: 600, flexShrink: 0 }}>
                {formatPriceBrl(item.price * item.quantity)}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
